import React, { useState } from 'react';
import { Form, Input, Select, Row, Col, Button, Space, Modal, Upload } from 'antd';
import { apiRequest } from '../../../service/apiRequest';
import { showSuccess, showError } from '../../../components/errorHandler';

interface TicketModalProps {
  visible: boolean;
  onCancel: () => void;
  onCreate: (ticketData: any) => void; // Callback for form submission
}

const TicketModal: React.FC<TicketModalProps> = ({ visible, onCancel, onCreate }) => {
  const [form] = Form.useForm();
  const [mainForm] = Form.useForm();
  const [isModalClose, setIsModalClose] = useState(true);
  const [fileList, setFileList] = useState<any[]>([]);


  // Check if any required fields are missing or invalid
  const isSubmitDisabled = () => {
    const errors = form.getFieldsError();
    return errors.some(({ errors }) => errors.length > 0); // If there are any validation errors, disable the button
  };

  const handleSubmit = async (values: any) => {
    try {
      const logFormData = (formData: FormData) => {
        for (let [key, value] of formData.entries()) {
          console.log(`${key}: ${value}`);
        }
      };
      const formData = new FormData();
      formData.append('ticket_type', values.ticket_type);
      formData.append('ticket_title', values.ticket_title);
      formData.append('ticket_description', values.ticket_description);

      fileList.forEach((file) => {
        formData.append('attachments', file.originFileObj); // backend expects 'attachments' array
      });
      logFormData(formData);
      const ticketData = await apiRequest({
        method: 'post',
        url: 'add-ticket',
        data: formData,
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      onCreate(ticketData); // Call the parent callback after successful API call
      showSuccess('Ticket created successfully!');
      form.resetFields(); // Reset the form fields after submission
      setFileList([]); // reset uploaded file 
      if (isModalClose) {
        onCancel();// This should update modal visibility state
      }// Close the modal after submission
    } catch (error) {
      showError('Failed to create ticket');
    }
  };
  



  return (
    <Modal
      title="Add New Ticket"
      visible={visible}
      onCancel={onCancel}
      footer={null}
      width="90%"
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit} // Handle form submission
        autoComplete="off"
      >
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="ticket_title"
              label={
                <span>
                  Ticket Title&nbsp;
                  <span style={{ fontSize: '12px', color: '#888' }}>(Max 255 characters)</span>
                </span>
              }
              rules={[{ required: true, message: 'Please enter the ticket title' }]}
            >
              <Input showCount maxLength={255} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="ticket_type"
              label="Ticket Type"
              rules={[{ required: true, message: 'Please select the ticket type' }]}
            >
              <Select
                showSearch
                filterSort={(optionA, optionB) =>
                  (optionA?.label ?? '').toLowerCase().localeCompare((optionB?.label ?? '').toLowerCase())
                }
                placeholder="Select ticket type"
                options={[
                  { value: 'bug_report', label: 'Bug Report' },
                  { value: 'feature_request', label: 'Feature Request' },
                  { value: 'incident', label: 'Incident' },
                  { value: 'support_request', label: 'Support Request' },
                  { value: 'maintenance', label: 'Maintenance' },
                  { value: 'enhancement', label: 'Enhancement' },
                  { value: 'complaint', label: 'Complaint' },
                  { value: 'general_inquiry', label: 'General Inquiry' },
                  { value: 'account_issue', label: 'Account Issue' },
                  { value: 'system_downtime', label: 'System Downtime' },
                  { value: 'security', label: 'Security' },
                  { value: 'integration_request', label: 'Integration Request' },
                  { value: 'other', label: 'Other' },
                ]}
              />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item
          name="ticket_description"
          label={
            <span>
              Ticket Description&nbsp;
              <span style={{ fontSize: '12px', color: '#888' }}>(Max 1000 characters)</span>
            </span>
          }
          rules={[{ required: true, message: 'Please enter the description' }]}
        >
          <Input.TextArea rows={4} showCount maxLength={1000} />
        </Form.Item>
        <Form.Item label="Attachments">
          <Upload
            fileList={fileList}
            onChange={({ fileList }) => setFileList(fileList)}
            beforeUpload={() => false} // prevent automatic upload
            multiple
            maxCount={5}
            listType="picture-card" // If you want image-style thumbnails
            showUploadList={{ showPreviewIcon: false }} // 👈 remove the hover preview icon
          >
            <Button>Click to Upload</Button>
          </Upload>
        </Form.Item>
        <Form.Item
          style={{ display: 'flex', justifyContent: 'flex-end' }} >
          <Space>
            <Button onClick={onCancel}>Cancel</Button>
            <Button
              type="primary"
              htmlType="submit"
              onClick={() => setIsModalClose(false)}
              disabled={isSubmitDisabled()} // Disable submit button if form is invalid
            >
              Submit & Create New Ticket
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              onClick={() => setIsModalClose(true)}
              disabled={isSubmitDisabled()} // Disable submit button if form is invalid
            >
              Submit
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default TicketModal;
